/* eslint-disable react/jsx-key */
/*
 * Copyright (c) 2023, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 LLC. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from 'react';
import { Box, List, ListItem, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import useStyles from 'components/ChoreoSystem/Authenticate/AuthMainLayout/AuthMainLayout.styles';
import LogoSvg from 'components/ChoreoSystem/Images/Various/LogoBlack.svg';
import { PRODUCT_NAME } from 'config';
import NotSupportedEngineMessage from 'modules/login/NotSupportedEngineMessage';

interface AuthMainLayoutProps {
  children: React.ReactNode;
  isMobile?: boolean;
  isSupportedEngine?: boolean;
}

function AuthMainLayout(props: AuthMainLayoutProps) {
  const { children, isMobile, isSupportedEngine } = props;
  const classes = useStyles();
  const getDisplayContent = () => {
    const title = (
      <FormattedMessage
        id="pages.Login.IDevP.title"
        defaultMessage="Get Started with {productName}"
        values={{
          productName: PRODUCT_NAME,
        }}
      />
    );
    const description = (
      <FormattedMessage
        id="pages.Login.IDevP.description"
        defaultMessage="A full-fledged platform for cloud native application development"
      />
    );
    const descriptionPoints = [
      <FormattedMessage
        id="pages.Login.IDevP.description.point1"
        defaultMessage="Design and develop applications"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point2"
        defaultMessage="Deploy and promote across environments"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point3"
        defaultMessage="Manage application configurations"
      />,
      <FormattedMessage
        id="pages.Login.IDevP.description.point4"
        defaultMessage="Observe and test an application"
      />,
    ];
    return (
      <Box>
        <Box mb={2}>
          <Typography variant="h3">{title}</Typography>
        </Box>
        <Typography variant="subtitle1">{description}</Typography>
        {descriptionPoints.length > 0 && (
          <List className={classes.descList} disablePadding>
            {descriptionPoints.map((point) => (
              <ListItem className={classes.descListItem} disableGutters>
                <Typography variant="subtitle1">{point}</Typography>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  };

  return (
    <Box className={classes.authMainLayoutContainer}>
      {!isMobile && (
        <Box className={classes.authMainLayoutDetails}>
          <Box className={classes.authMainLayoutDetailsCont}>
            <Box className={classes.logoWrap}>
              <img
                alt={`${PRODUCT_NAME} Logo`}
                src={LogoSvg}
                className={classes.logoImage}
              />
            </Box>
            {getDisplayContent()}
          </Box>
          <Box className={classes.authBgImg} />
        </Box>
      )}

      <Box
        className={clsx({
          [classes.authMainLayoutForm]: true,
          [classes.authMainLayoutFormMobile]: isMobile,
        })}
      >
        <Box className={classes.authMainLayoutFormContent}>{children}</Box>

        {!isSupportedEngine && <NotSupportedEngineMessage />}
      </Box>
    </Box>
  );
}

export default AuthMainLayout;
